import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import VerificationModal from '../components/VerificationModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  isSuspended: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
  _count: {
    accounts: number;
    transactions: number;
    debtAccounts: number;
  };
}

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'analytics'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'delete' | 'changeEmail';
    userId: string;
    data?: any;
  } | null>(null);
  
  const [editingEmail, setEditingEmail] = useState<{ userId: string; newEmail: string } | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const suspendUser = async (userId: string) => {
    try {
      await api.put(`/admin/user/${userId}/suspend`);
      setSuccess('Benutzer wurde gesperrt');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Sperren');
    }
  };

  const unsuspendUser = async (userId: string) => {
    try {
      await api.put(`/admin/user/${userId}/unsuspend`);
      setSuccess('Benutzer wurde entsperrt');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Entsperren');
    }
  };

  const requestCode = async () => {
    try {
      await api.post('/admin/request-code');
      setShowVerificationModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden des Codes');
    }
  };

  const initiateDeleteUser = async (userId: string) => {
    setPendingAction({ type: 'delete', userId });
    await requestCode();
  };

  const initiateChangeEmail = async (userId: string, newEmail: string) => {
    setPendingAction({ type: 'changeEmail', userId, data: { newEmail } });
    await requestCode();
  };

  const handleVerification = async (code: string) => {
    if (!pendingAction) return;

    if (pendingAction.type === 'delete') {
      await api.delete(`/admin/user/${pendingAction.userId}`, { data: { code } });
      setSuccess('Benutzer wurde gelöscht');
      loadUsers();
    } else if (pendingAction.type === 'changeEmail') {
      await api.put(`/admin/user/${pendingAction.userId}/change-email`, {
        newEmail: pendingAction.data.newEmail,
        code
      });
      setSuccess('E-Mail-Adresse wurde geändert');
      setEditingEmail(null);
      loadUsers();
    }

    setPendingAction(null);
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="container" style={{ marginTop: '40px' }}>Lädt...</div>;
  }

  return (
    <div className="container" style={{ maxWidth: '1200px', marginTop: '40px' }}>
      <h1 style={{ marginBottom: '30px' }}>🛡️ Admin-Bereich</h1>

      {error && <div className="error">{error}</div>}
      {success && <div className="success" style={{
        padding: '12px',
        background: '#d4edda',
        color: '#155724',
        border: '1px solid #c3e6cb',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>{success}</div>}
      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '30px',
        borderBottom: '2px solid #e9ecef'
      }}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'users' ? '3px solid #667eea' : '3px solid transparent',
            color: activeTab === 'users' ? '#667eea' : '#666',
            fontWeight: activeTab === 'users' ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          👥 Benutzer
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'analytics' ? '3px solid #667eea' : '3px solid transparent',
            color: activeTab === 'analytics' ? '#667eea' : '#666',
            fontWeight: activeTab === 'analytics' ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          📊 Analytics
        </button>
      </div>

      {activeTab === 'users' && (
        <>
      {/* 
      {/* Suche */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="🔍 Benutzer suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '16px'
          }}
        />
      </div>

      {/* User-Tabelle */}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>E-Mail</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>
                  {user.firstName} {user.lastName}
                  {user.isAdmin && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      background: '#667eea',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      ADMIN
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px' }}>
                  {editingEmail?.userId === user.id ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="email"
                        value={editingEmail.newEmail}
                        onChange={(e) => setEditingEmail({ ...editingEmail, newEmail: e.target.value })}
                        style={{ flex: 1, padding: '4px 8px' }}
                      />
                      <button
                        className="btn btn-primary"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                        onClick={() => initiateChangeEmail(user.id, editingEmail.newEmail)}
                      >
                        ✓
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                        onClick={() => setEditingEmail(null)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {user.email}
                      <button
                        onClick={() => setEditingEmail({ userId: user.id, newEmail: user.email })}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="E-Mail bearbeiten"
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  {user.isSuspended ? (
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>🚫 Gesperrt</span>
                  ) : (
                    <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Aktiv</span>
                  )}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    {user.isSuspended ? (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => unsuspendUser(user.id)}
                      >
                        Entsperren
                      </button>
                    ) : (
                      <button
                        className="btn"
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          background: '#ffc107',
                          color: '#000',
                          border: 'none'
                        }}
                        onClick={() => suspendUser(user.id)}
                      >
                        Sperren
                      </button>
                    )}
                    <button
                      className="btn"
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none'
                      }}
                      onClick={() => {
                        if (window.confirm(`Benutzer "${user.firstName} ${user.lastName}" wirklich löschen?`)) {
                          initiateDeleteUser(user.id);
                        }
                      }}
                    >
                      Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            Keine Benutzer gefunden
          </div>
        )}
      </div>
      </>
      )}

      {activeTab === 'analytics' && <AnalyticsTab />}

      <VerificationModal
        isOpen={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          setPendingAction(null);
        }}
        onVerify={handleVerification}
        title="Admin-Bestätigung"
        message="Aus Sicherheitsgründen benötigen wir einen Bestätigungscode von dir."
      />
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/analytics/stats', {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      });
      console.log('Analytics response:', response.data);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('Fehler beim Laden der Analytics:', error);
      setError(error.response?.data?.message || 'Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Lädt Analytics...</div>;
  }

  if (error) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
        <p style={{ color: '#dc2626', marginBottom: '20px' }}>{error}</p>
        <button 
          onClick={loadStats}
          className="btn btn-primary"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!stats || stats.totalViews === 0) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
        <h3 style={{ marginBottom: '10px' }}>Noch keine Daten</h3>
        <p style={{ color: '#666' }}>
          Es wurden noch keine Seitenaufrufe getrackt. Besuche die Seite, um Daten zu sammeln.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Date Range Picker */}
      <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
        <h3 style={{ marginBottom: '15px' }}>📅 Zeitraum</h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Von</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Bis</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
            />
          </div>
        </div>
      </div>

      {/* Übersicht */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#667eea' }}>{stats.totalViews.toLocaleString()}</div>
          <div style={{ color: '#666', marginTop: '5px' }}>Seitenaufrufe</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#764ba2' }}>{stats.uniqueVisitors.toLocaleString()}</div>
          <div style={{ color: '#666', marginTop: '5px' }}>Unique Visitors</div>
        </div>
        {stats.totalEvents !== undefined && (
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#f093fb' }}>{stats.totalEvents.toLocaleString()}</div>
            <div style={{ color: '#666', marginTop: '5px' }}>Events</div>
          </div>
        )}
      </div>

      {/* Device & Browser Stats */}
      {stats.deviceStats && stats.deviceStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div className="card">
            <h3 style={{ marginBottom: '15px' }}>📱 Geräte</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {stats.deviceStats.map((device: any, idx: number) => {
                const total = stats.deviceStats.reduce((sum: number, d: any) => sum + d.count, 0);
                const percentage = ((device.count / total) * 100).toFixed(1);
                return (
                  <div key={idx}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>{device.device === 'mobile' ? '📱 Mobile' : '💻 Desktop'}</span>
                      <span style={{ fontWeight: 'bold' }}>{device.count} ({percentage}%)</span>
                    </div>
                    <div style={{ background: '#eee', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                      <div style={{ background: device.device === 'mobile' ? '#667eea' : '#764ba2', width: `${percentage}%`, height: '100%' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {stats.browserStats && stats.browserStats.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: '15px' }}>🌐 Browser</h3>
              <table style={{ width: '100%', fontSize: '14px' }}>
                <tbody>
                  {stats.browserStats.map((browser: any, idx: number) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{browser.browser}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{browser.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Top Events */}
      {stats.eventsByName && stats.eventsByName.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>⚡ Top Events</h3>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Event</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Anzahl</th>
              </tr>
            </thead>
            <tbody>
              {stats.eventsByName.map((event: any, idx: number) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px' }}>{event.event.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>{event.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Seiten */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '15px' }}>🏆 Top Seiten</h3>
        <table style={{ width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Seite</th>
              <th style={{ padding: '10px', textAlign: 'right' }}>Aufrufe</th>
            </tr>
          </thead>
          <tbody>
            {stats.viewsByPage.map((page: any, idx: number) => (
              <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px' }}>{page.path}</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>{page.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Zeitverlauf */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '15px' }}>📈 Zeitverlauf (letzte 30 Tage)</h3>
        <table style={{ width: '100%', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>Datum</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Aufrufe</th>
            </tr>
          </thead>
          <tbody>
            {stats.viewsByDay.slice(0, 10).map((day: any, idx: number) => (
              <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px' }}>{new Date(day.date).toLocaleDateString('de-DE')}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{day.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Top Referers */}
      {stats.topReferers.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>🔗 Top Referrer</h3>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Quelle</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Besucher</th>
              </tr>
            </thead>
            <tbody>
              {stats.topReferers.map((ref: any, idx: number) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px', fontSize: '12px', wordBreak: 'break-all' }}>{ref.referer}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{ref.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Admin;
